import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const AdSpendEntry = () => {
  const [searchParams] = useSearchParams();
  const subaccountId = searchParams.get('subaccount_id');
  const [activeTab, setActiveTab] = useState('entry'); // 'entry' or 'manage'
  const [entryType, setEntryType] = useState('daily'); // 'daily' or 'bulk'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York'); // Default timezone

  // Management state
  const [adSpendRecords, setAdSpendRecords] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Filter state
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState({
    start: '',
    end: ''
  });

  // Function to get yesterday's date in the specified timezone
  const getYesterdayDate = (tz = timezone) => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return yesterday.toISOString().split('T')[0];
  };

  // Daily entry state
  const [dailyEntry, setDailyEntry] = useState({
    date: getYesterdayDate(),
    facebook: '',
    google: '',
    tiktok: ''
  });

  // Bulk entry state
  const [bulkEntry, setBulkEntry] = useState({
    start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    end_date: getYesterdayDate(), // Yesterday
    facebook_total: '',
    google_total: '',
    tiktok_total: ''
  });

  // Fetch location timezone on component mount
  useEffect(() => {
    const fetchLocationTimezone = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/dashboard/${subaccountId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.location && data.location.timezone) {
            setTimezone(data.location.timezone);
            // Update dates with correct timezone
            const yesterdayInTz = getYesterdayDate(data.location.timezone);
            setDailyEntry(prev => ({ ...prev, date: yesterdayInTz }));
            setBulkEntry(prev => ({ ...prev, end_date: yesterdayInTz }));
          }
        }
      } catch (err) {
        console.log('Could not fetch location timezone, using default');
      }
    };

    if (subaccountId) {
      fetchLocationTimezone();
    }
  }, [subaccountId]);

  // Load records when switching to manage tab
  useEffect(() => {
    if (activeTab === 'manage') {
      fetchAdSpendRecords();
    }
  }, [activeTab]);

  const fetchAdSpendRecords = async (page = 1, limit = recordsPerPage, platform = filterPlatform, dateRange = filterDateRange) => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      let url = `${backendUrl}/api/ad-spend/all/${subaccountId}?page=${page}&limit=${limit}`;
      
      if (platform && platform !== 'all') {
        url += `&platform=${platform}`;
      }
      
      if (dateRange.start) {
        url += `&start_date=${dateRange.start}`;
      }
      
      if (dateRange.end) {
        url += `&end_date=${dateRange.end}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAdSpendRecords(data.records || []);
        setTotalRecords(data.total || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      setError('Failed to fetch ad spend records');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/ad-spend/${recordId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Record deleted successfully');
        fetchAdSpendRecords(currentPage, recordsPerPage, filterPlatform, filterDateRange);
      } else {
        throw new Error('Failed to delete record');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Bulk delete functions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(adSpendRecords.map(record => record._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRecord = (recordId) => {
    if (selectedRecords.includes(recordId)) {
      setSelectedRecords(selectedRecords.filter(id => id !== recordId));
    } else {
      setSelectedRecords([...selectedRecords, recordId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) {
      setError('No records selected for deletion');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRecords.length} record(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/ad-spend/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          record_ids: selectedRecords
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Successfully deleted ${data.deleted_count} record(s)`);
        setSelectedRecords([]);
        setSelectAll(false);
        fetchAdSpendRecords(currentPage, recordsPerPage, filterPlatform, filterDateRange);
      } else {
        throw new Error('Failed to delete records');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter functions
  const handleFilterChange = () => {
    setCurrentPage(1);
    fetchAdSpendRecords(1, recordsPerPage, filterPlatform, filterDateRange);
  };

  const clearFilters = () => {
    setFilterPlatform('all');
    setFilterDateRange({ start: '', end: '' });
    setCurrentPage(1);
    fetchAdSpendRecords(1, recordsPerPage, 'all', { start: '', end: '' });
  };

  // Pagination functions
  const handlePageChange = (page) => {
    fetchAdSpendRecords(page, recordsPerPage, filterPlatform, filterDateRange);
  };

  const handleRecordsPerPageChange = (newLimit) => {
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
    fetchAdSpendRecords(1, newLimit, filterPlatform, filterDateRange);
  };

  const submitDailyEntry = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const entries = [];
      if (dailyEntry.facebook) entries.push({ date: dailyEntry.date, platform: 'facebook', amount: parseFloat(dailyEntry.facebook) });
      if (dailyEntry.google) entries.push({ date: dailyEntry.date, platform: 'google', amount: parseFloat(dailyEntry.google) });
      if (dailyEntry.tiktok) entries.push({ date: dailyEntry.date, platform: 'tiktok', amount: parseFloat(dailyEntry.tiktok) });
      
      if (entries.length === 0) {
        setError('Please enter at least one ad spend amount');
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/ad-spend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_id: subaccountId,
          entries: entries
        })
      });

      if (response.ok) {
        setSuccess('Ad spend entries added successfully!');
        setDailyEntry({ date: getYesterdayDate(), facebook: '', google: '', tiktok: '' });
      } else {
        throw new Error('Failed to add ad spend entries');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkEntry = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/ad-spend/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_id: subaccountId,
          ...bulkEntry
        })
      });

      if (response.ok) {
        setSuccess('Bulk ad spend entries created successfully!');
        setBulkEntry({
          start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          end_date: getYesterdayDate(),
          facebook_total: '',
          google_total: '',
          tiktok_total: ''
        });
      } else {
        throw new Error('Failed to create bulk entries');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRecord = async (recordId, updatedRecord) => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/ad-spend/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedRecord)
      });

      if (response.ok) {
        setSuccess('Record updated successfully');
        setEditingRecord(null);
        fetchAdSpendRecords();
      } else {
        throw new Error('Failed to update record');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!subaccountId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Missing Location ID</h2>
            <p className="text-gray-600 mb-6">This page requires a valid subaccount ID.</p>
            <a 
              href="/"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">💰 Ad Spend Entry</h1>
              <p className="text-gray-600 mt-1">Track your advertising investments</p>
            </div>
            <a 
              href={`/reports?subaccount_id=${subaccountId}`}
              className="text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors"
              style={{backgroundColor: '#d4161d'}}
            >
              📊 View Reports
            </a>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('entry')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'entry'
                  ? 'text-white border-b-2' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={activeTab === 'entry' ? {borderColor: '#d4161d', color: '#d4161d'} : {}}
            >
              📝 Add Entry
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'manage'
                  ? 'text-white border-b-2'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={activeTab === 'manage' ? {borderColor: '#d4161d', color: '#d4161d'} : {}}
            >
              📋 Manage Records
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600 text-sm">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Entry Tab */}
        {activeTab === 'entry' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">📅 Add Ad Spend</h2>
            
            {/* Entry Type Selection */}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setEntryType('daily')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  entryType === 'daily' 
                    ? 'text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                style={entryType === 'daily' ? {backgroundColor: '#52aef2'} : {}}
              >
                📅 Daily Entry
              </button>
              <button
                onClick={() => setEntryType('bulk')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  entryType === 'bulk' 
                    ? 'text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                style={entryType === 'bulk' ? {backgroundColor: '#52aef2'} : {}}
              >
                📊 Bulk Entry
              </button>
            </div>

            {/* Daily Entry Form */}
            {entryType === 'daily' && (
              <form onSubmit={submitDailyEntry} className="mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={dailyEntry.date}
                      onChange={(e) => setDailyEntry({ ...dailyEntry, date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔵 Facebook Ad Spend
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={dailyEntry.facebook}
                        onChange={(e) => setDailyEntry({ ...dailyEntry, facebook: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔴 Google Ad Spend
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={dailyEntry.google}
                        onChange={(e) => setDailyEntry({ ...dailyEntry, google: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ⚫ TikTok Ad Spend
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={dailyEntry.tiktok}
                        onChange={(e) => setDailyEntry({ ...dailyEntry, tiktok: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className="text-white px-8 py-3 rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                    style={{backgroundColor: '#d4161d'}}
                  >
                    {loading ? 'Saving...' : '💾 Save Daily Spend'}
                  </button>
                </div>
              </form>
            )}

            {/* Bulk Entry Form */}
            {entryType === 'bulk' && (
              <form onSubmit={submitBulkEntry} className="mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={bulkEntry.start_date}
                      onChange={(e) => setBulkEntry({ ...bulkEntry, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={bulkEntry.end_date}
                      onChange={(e) => setBulkEntry({ ...bulkEntry, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔵 Facebook Total
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={bulkEntry.facebook_total}
                        onChange={(e) => setBulkEntry({ ...bulkEntry, facebook_total: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🔴 Google Total
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={bulkEntry.google_total}
                        onChange={(e) => setBulkEntry({ ...bulkEntry, google_total: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ⚫ TikTok Total
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={bulkEntry.tiktok_total}
                        onChange={(e) => setBulkEntry({ ...bulkEntry, tiktok_total: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className="text-white px-8 py-3 rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                    style={{backgroundColor: '#f9d548', color: '#333'}}
                  >
                    {loading ? 'Processing...' : '📊 Create Bulk Entries'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Management Tab */}
        {activeTab === 'manage' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">📋 Ad Spend Records</h2>
              <div className="flex space-x-4">
                {selectedRecords.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={loading}
                    className="text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                    style={{backgroundColor: '#d4161d'}}
                  >
                    {loading ? 'Deleting...' : `🗑️ Delete Selected (${selectedRecords.length})`}
                  </button>
                )}
                <button
                  onClick={() => fetchAdSpendRecords(currentPage, recordsPerPage, filterPlatform, filterDateRange)}
                  disabled={loading}
                  className="text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                  style={{backgroundColor: '#52aef2'}}
                >
                  {loading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform
                  </label>
                  <select
                    value={filterPlatform}
                    onChange={(e) => setFilterPlatform(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Platforms</option>
                    <option value="facebook">Facebook</option>
                    <option value="google">Google</option>
                    <option value="tiktok">TikTok</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filterDateRange.start}
                    onChange={(e) => setFilterDateRange({ ...filterDateRange, start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filterDateRange.end}
                    onChange={(e) => setFilterDateRange({ ...filterDateRange, end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleFilterChange}
                    className="text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
                    style={{backgroundColor: '#f9d548', color: '#333'}}
                  >
                    🔍 Filter
                  </button>
                  <button
                    onClick={clearFilters}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {adSpendRecords.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No ad spend records found.</p>
              </div>
            ) : (
              <div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {adSpendRecords.map((record) => (
                        <tr key={record._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record._id)}
                              onChange={() => handleSelectRecord(record._id)}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {editingRecord === record._id ? (
                              <input
                                type="date"
                                value={record.date}
                                onChange={(e) => {
                                  const newRecords = adSpendRecords.map(r => 
                                    r._id === record._id ? { ...r, date: e.target.value } : r
                                  );
                                  setAdSpendRecords(newRecords);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              new Date(record.date).toLocaleDateString()
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                            {editingRecord === record._id ? (
                              <select
                                value={record.platform}
                                onChange={(e) => {
                                  const newRecords = adSpendRecords.map(r => 
                                    r._id === record._id ? { ...r, platform: e.target.value } : r
                                  );
                                  setAdSpendRecords(newRecords);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="facebook">Facebook</option>
                                <option value="google">Google</option>
                                <option value="tiktok">TikTok</option>
                              </select>
                            ) : (
                              record.platform
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {editingRecord === record._id ? (
                              <input
                                type="number"
                                step="0.01"
                                value={record.amount}
                                onChange={(e) => {
                                  const newRecords = adSpendRecords.map(r => 
                                    r._id === record._id ? { ...r, amount: parseFloat(e.target.value) } : r
                                  );
                                  setAdSpendRecords(newRecords);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm w-24"
                              />
                            ) : (
                              `$${record.amount.toFixed(2)}`
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {editingRecord === record._id ? (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => updateRecord(record._id, record)}
                                  className="text-green-600 hover:text-green-800"
                                >
                                  ✅ Save
                                </button>
                                <button
                                  onClick={() => setEditingRecord(null)}
                                  className="text-gray-600 hover:text-gray-800"
                                >
                                  ❌ Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setEditingRecord(record._id)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => handleSingleDelete(record._id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">{((currentPage - 1) * recordsPerPage) + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * recordsPerPage, totalRecords)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{totalRecords}</span>
                        {' '}results
                      </p>
                      <div className="ml-4 flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Show:</label>
                        <select
                          value={recordsPerPage}
                          onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="text-sm text-gray-700">per page</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, Math.ceil(totalRecords / recordsPerPage)) }, (_, i) => {
                        const totalPages = Math.ceil(totalRecords / recordsPerPage);
                        const startPage = Math.max(1, currentPage - 2);
                        const page = startPage + i;
                        
                        if (page > totalPages) return null;
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 border rounded text-sm ${
                              page === currentPage
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(totalRecords / recordsPerPage)}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdSpendEntry;